import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { desktopApi } from '../../lib/desktopApi';
import ConsignesMarkdown from '../ConsignesMarkdown';
import { formatStamp } from '../../lib/time';

const HELP = [
  ['## Titre', 'sous-titre visible'],
  ['### Titre', 'bloc à ouvrir (article)'],
  ['- point', 'liste ; "  - " pour un sous-point'],
  ['1. étape', 'étapes numérotées'],
  ['> texte', 'encadré attention'],
  ['**gras**  *italique*', ''],
  ['[027 720 13 13](tel:+41277201313)', 'numéro à toucher'],
  ['[nom@vatel.ch](mailto:nom@vatel.ch)', 'adresse e-mail'],
];

const ConsignesEditor = ({ adminToken }) => {
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [drafts, setDrafts] = useState({});
  const [mode, setMode] = useState('edit');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!adminToken) return;
    desktopApi.getConsignes(adminToken)
      .then((data) => {
        const list = data?.sections || [];
        setSections(list);
        setActiveId((current) => current || list[0]?.id || '');
      })
      .catch(() => toast.error('Consignes indisponibles'));
  }, [adminToken]);

  const active = sections.find((section) => section.id === activeId);
  const text = active ? (drafts[active.id] ?? active.body) : '';
  const dirty = active && drafts[active.id] !== undefined && drafts[active.id] !== active.body;

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const saved = await desktopApi.updateConsigneSection(active.id, text, adminToken);
      setSections((list) => list.map((section) => (section.id === saved.id ? saved : section)));
      setDrafts((all) => {
        const next = { ...all };
        delete next[active.id];
        return next;
      });
      toast.success('Consignes enregistrées');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="consignes-editor">
      <p className="mb-4 text-sm text-muted-foreground">
        Textes de l'onglet Consignes, lus par tous les agents connectés. Choisissez une section, modifiez le texte, vérifiez l'aperçu, puis enregistrez.
      </p>
      <div className="mb-4 flex flex-wrap gap-2">
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveId(section.id)}
            data-testid={`consignes-editor-tab-${section.id}`}
            className={`min-h-11 rounded-md border px-3 text-sm font-semibold ${section.id === activeId ? 'border-primary bg-primary/15 text-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
          >
            {section.title}
            {drafts[section.id] !== undefined && drafts[section.id] !== section.body ? ' •' : ''}
          </button>
        ))}
      </div>
      {active && (
        <>
          <div className="mb-3 flex gap-2 lg:hidden">
            {[['edit', 'Modifier'], ['preview', 'Aperçu']].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={`min-h-11 flex-1 rounded-md border text-sm font-semibold ${mode === id ? 'border-primary bg-primary/15 text-primary' : 'border-border text-muted-foreground'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className={mode === 'edit' ? '' : 'hidden lg:block'}>
              <label htmlFor="consignes-editor-text" className="mb-1 block text-sm font-semibold">Texte (Markdown simple)</label>
              <textarea
                id="consignes-editor-text"
                value={text}
                onChange={(event) => setDrafts((all) => ({ ...all, [active.id]: event.target.value }))}
                spellCheck
                data-testid="consignes-editor-text"
                className="h-[60vh] min-h-[320px] w-full resize-y rounded-md border border-input bg-transparent p-3 font-mono text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className={mode === 'preview' ? '' : 'hidden lg:block'}>
              <p className="mb-1 text-sm font-semibold">Aperçu</p>
              <div className="h-[60vh] min-h-[320px] overflow-y-auto rounded-md border border-border bg-background p-4" data-testid="consignes-editor-preview">
                <ConsignesMarkdown body={text} sectionId={`apercu-${active.id}`} />
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="consignes-editor-save"
              className="h-11 rounded-md bg-primary px-6 font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer cette section'}
            </button>
            {dirty && (
              <button
                type="button"
                onClick={() => setDrafts((all) => {
                  const next = { ...all };
                  delete next[active.id];
                  return next;
                })}
                className="h-11 rounded-md border border-border px-4 text-sm font-semibold text-muted-foreground"
              >
                Annuler les modifications
              </button>
            )}
            <span className="text-xs text-muted-foreground">
              {active.updated_at ? `Dernière modification le ${formatStamp(active.updated_at)}${active.updated_by ? ` par ${active.updated_by}` : ''}` : ''}
            </span>
          </div>
          <details className="mt-4 rounded-md border border-border p-3 text-sm">
            <summary className="min-h-11 cursor-pointer py-2 font-semibold">Aide de mise en forme</summary>
            <ul className="mt-2 space-y-1">
              {HELP.map(([code, hint]) => (
                <li key={code}><code className="rounded bg-zinc-800 px-1.5 py-0.5">{code}</code>{hint ? ` : ${hint}` : ''}</li>
              ))}
            </ul>
          </details>
        </>
      )}
    </div>
  );
};

export default ConsignesEditor;
